import { useEffect, useRef, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { useToast } from "@/components/ui/use-toast";

const isActiveElementEditable = () => {
  const element = document.activeElement;
  return (
    element instanceof HTMLInputElement ||
    element instanceof HTMLTextAreaElement ||
    element instanceof HTMLSelectElement ||
    element?.attributes.getNamedItem("contenteditable")?.value === "true"
  );
};

export const useFocusMode = () => {
  const [enabled, setEnabled] = useState(false);
  const { toast, dismiss } = useToast();
  const currentToastId = useRef<string>(null!);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (isActiveElementEditable()) return;

      if (
        event.key === "f" &&
        !event.ctrlKey &&
        !event.shiftKey &&
        !event.altKey &&
        !event.metaKey
      ) {
        setEnabled((prev) => {
          if (!prev) {
            const { id } = toast({
              variant: "default",
              title: "Focus Mode Enabled",
              description: (
                <div className="mt-2">
                  Press the{" "}
                  <Badge variant="outline" className="mx-2 inline-flex">
                    F
                  </Badge>{" "}
                  key to toggle it on/off.
                </div>
              ),
              duration: 9999999,
            });
            currentToastId.current = id;
          } else {
            dismiss(currentToastId.current);
          }

          return !prev;
        });
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  return {
    enabled,
    focusedClassName: enabled
      ? "opacity-10 grayscale blur-xs transition-opacity duration-300 ease-linear"
      : "",
  };
};
